import { useState, useEffect, useMemo, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import StepIndicator from '../components/ui/StepIndicator.jsx';
import Icon from '../components/ui/Icon.jsx';
import ProductImage from '../components/ui/ProductImage.jsx';
import { useCart } from '../context/CartContext.jsx';
import { useToast } from '../context/ToastContext.jsx';
import { getProduct } from '../api/productsApi.js';
import { FREE_SHIPPING_THRESHOLD, SHIPPING_FEE } from '../api/ordersApi.js';
import { analyticsEvent, gaItem } from '../utils/analytics.js';
import './CartPage.css';

// 數量輸入框：可直接打數字（只收數字），失焦時修正成合法值；+/- 也可用
function QtyInput({ qty, max, onCommit }) {
  const [draft, setDraft] = useState(null); // null = 未在編輯，顯示購物車的值
  const value = draft ?? qty;
  const commit = (v) => {
    const n = Math.max(1, Number(v) || 1);
    onCommit(typeof max === 'number' ? Math.min(n, Math.max(max, 1)) : n);
    setDraft(null);
  };
  return (
    <input
      value={value}
      inputMode="numeric"
      aria-label="數量"
      onChange={e => setDraft(e.target.value.replace(/\D/g, ''))}
      onBlur={() => commit(value)}
      onKeyDown={e => { if (e.key === 'Enter') e.target.blur(); }}
    />
  );
}

function CartPage() {
  const { items, updateQty, setQty, removeFromCart, subtotal, totalCount } = useCart();
  const { showToast } = useToast();
  const [shipping, setShipping] = useState('711');
  // 各規格的最新庫存 { variantId: stock }；null = 還在載入
  const [stockMap, setStockMap] = useState(null);
  const navigate = useNavigate();

  const initialCart = useRef({ items, subtotal });

  useEffect(() => {
    const cart = initialCart.current;
    if (cart.items.length === 0) return;
    analyticsEvent('view_cart', {
      currency: 'TWD',
      value: cart.subtotal,
      items: cart.items.map(gaItem),
    });
  }, []);
  // 進購物車就抓最新庫存，超量在這裡提醒，不要等到結帳最後一步才被後端打回
  const productIdsKey = useMemo(() => [...new Set(
    items.filter(i => i.variantId && i.kind !== 'virtual').map(i => i.productId)
  )].sort().join(','), [items]);

  useEffect(() => {
    if (!productIdsKey) { setStockMap({}); return; }
    let alive = true;
    Promise.all(productIdsKey.split(',').map(id => getProduct(id).catch(() => null)))
      .then(products => {
        if (!alive) return;
        const map = {};
        products.forEach(p => (p?.variants || []).forEach(v => {
          if (typeof v.stock === 'number') map[v.id] = v.stock;
        }));
        setStockMap(map);
      });
    return () => { alive = false; };
  }, [productIdsKey]);

  const stockOf = (item) => stockMap?.[item.variantId];
  const stockIssue = (item) => {
    const s = stockOf(item);
    if (typeof s !== 'number') return null;
    if (s === 0) return '已售完，請移除此商品';
    if (item.qty > s) return `庫存不足，僅剩 ${s} 件`;
    return null;
  };
  const hasStockIssue = items.some(i => stockIssue(i) !== null);

  const handleRemove = (lineId, name) => {
    removeFromCart(lineId);
    const removedItem = items.find(item => (item.lineId ?? item.id) === lineId);
    if (removedItem) {
      analyticsEvent('remove_from_cart', {
        currency: 'TWD',
        value: Number(removedItem.price ?? 0) * Number(removedItem.qty ?? 1),
        items: [gaItem(removedItem, 0)],
      });
    }
    showToast(`已移除：${name.slice(0, 20)}…`, 'info', 2500);
  };

  const shippingCost = subtotal >= FREE_SHIPPING_THRESHOLD ? 0 : SHIPPING_FEE;
  const total = subtotal + shippingCost;

  if (items.length === 0) {
    return (
      <main className="cart-page">
        <div className="cart-head">
          <h1>購物車 <small>YOUR CART · 0 件商品</small></h1>
        </div>
        <div className="empty-cart">
          <p>購物車是空的，去逛逛吧。</p>
          <Link className="checkout-btn" to="/shop" style={{ maxWidth: 280, margin: '24px auto 0' }}>
            前往商品一覽 <Icon name="arrow" size={18} />
          </Link>
        </div>
      </main>
    );
  }

  return (
    <main className="cart-page">
      <div className="cart-head">
        <h1>購物車 <small>YOUR CART · {totalCount} 件商品</small></h1>
      </div>

      <StepIndicator current={1} />

      <div className="cart-grid">
        <div>
          <div className="items">
            <div className="items-head">
              <div>商品</div><div>單價</div><div>數量</div><div style={{ textAlign: 'right' }}>小計</div><div></div>
            </div>
            {items.map(item => (
              <div className="cart-item" key={item.lineId ?? item.id}>
                <div className="item-main">
                  <div className="item-thumb">
                    <ProductImage src={item.image} alt={item.name}
                      imgStyle={{ width: '100%', height: '100%', objectFit: 'contain' }} />
                  </div>
                  <div className="item-text">
                    <h3>{item.name}</h3>
                    {item.tags && (
                      <div className="item-meta">
                        {item.tags.map(t => <span key={t}>{t}</span>)}
                      </div>
                    )}
                  </div>
                </div>
                <div className="unit-price">NT${item.price.toLocaleString()}</div>
                <div>
                  <div className="qty-mini">
                    <button onClick={() => updateQty(item.lineId ?? item.id, -1)} aria-label="減少">−</button>
                    <QtyInput
                      qty={item.qty}
                      max={stockOf(item)}
                      onCommit={n => setQty(item.lineId ?? item.id, n)}
                    />
                    <button
                      onClick={() => {
                        const s = stockOf(item);
                        if (typeof s === 'number' && item.qty + 1 > s) {
                          showToast(`庫存不足：僅剩 ${s} 件`, 'error', 2500);
                          return;
                        }
                        updateQty(item.lineId ?? item.id, 1);
                      }}
                      aria-label="增加"
                    >+</button>
                  </div>
                  {stockIssue(item) && (
                    <div style={{ font: '700 11px "Noto Sans TC", sans-serif', color: 'var(--red)', marginTop: 4, textAlign: 'center' }}>
                      {stockIssue(item)}
                    </div>
                  )}
                </div>
                <div className="line-total">NT${(item.price * item.qty).toLocaleString()}</div>
                <button className="remove-btn" onClick={() => handleRemove(item.lineId ?? item.id, item.name)} aria-label="移除商品">×</button>
              </div>
            ))}
            <div className="items-foot">
              <Link to="/shop">
                <Icon name="arrow-l" size={16} /> 繼續購物
              </Link>
            </div>
          </div>
        </div>

        <div className="aside-stack">
          <div className="summary">
            <h3>訂單摘要</h3>
            <div className="summary-row"><span>商品小計</span><span>NT${subtotal.toLocaleString()}</span></div>
            <div className="summary-row muted">
              <span>運費</span>
              <span>{shippingCost === 0 ? '免運' : `NT$${shippingCost}`}</span>
            </div>
            {shippingCost > 0 && (
              <div className="ship-note">
                <Icon name="truck" size={14} /> 再買 NT${(1000 - subtotal).toLocaleString()} 即享免運
              </div>
            )}
            <div className="summary-row total"><span>合計</span><b>NT${total.toLocaleString()}</b></div>
            {hasStockIssue && (
              <div style={{ font: '700 12px "Noto Sans TC", sans-serif', color: 'var(--red)', margin: '8px 0' }}>
                部分商品庫存不足，請調整數量後再結帳
              </div>
            )}
            <button
              className="checkout-btn"
              disabled={hasStockIssue}
              style={hasStockIssue ? { opacity: 0.5, cursor: 'not-allowed' } : undefined}
              onClick={() => navigate('/checkout')}
            >
              前往結帳 <Icon name="arrow" size={18} />
            </button>
            <div className="trust-row">
            </div>
          </div>

          <div className="shipping-card">
            <h4>配送方式</h4>
            {[
              { id: '711', label: '7-11 取貨', sub: '超商取貨 · 2–4 個工作天' },
            ].map(s => (
              <div key={s.id} className={`ship-opt${shipping === s.id ? ' on' : ''}`} onClick={() => setShipping(s.id)}>
                <div className="radio"></div>
                <div><b>{s.label}</b><small>{s.sub}</small></div>
                <div className="price">{shippingCost === 0 ? '免運' : `NT$${shippingCost}`}</div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </main>
  );
}

export default CartPage;
